import { supabase, toError } from '@/lib/supabase'
import type { ProductComponent, ProductComponentInsert, ProductComponentUpdate } from '@/types/database'
import { profileService } from './profileService'
import { activityLogService } from './activityLogService'

// Extended type that includes component details
export interface ProductComponentWithDetails extends ProductComponent {
  component: {
    id: string
    name: string
    part_number: string
    category: string
    manufacturer_name: string | null
    ce_marked: boolean
    ukca_marked: boolean
    unit_price: number | null
    currency: string
    stock_quantity: number
    min_stock_level: number | null
    location: string | null
    purpose: string | null
  }
  added_by_profile?: { full_name: string | null } | null
}

// Tree node structure for BOM visualization
export interface BomTreeNode {
  id: string
  componentId: string
  name: string
  partNumber: string
  category: string
  quantity: number
  unit: string
  referenceDesignator: string | null
  ceMarked: boolean
  ukcaMarked: boolean
  stockQuantity: number
  unitPrice: number | null
  currency: string
  level: number
  children: BomTreeNode[]
  isLowStock: boolean
  supplierName: string | null
  // Legacy alias retained for compatibility with older callers/tests
  manufacturerName?: string | null
}

export const productComponentService = {
  async syncProductSuppliersFromComponents(
    productId: string,
    componentIds: string[]
  ): Promise<void> {
    const uniqueComponentIds = [...new Set(componentIds.filter(Boolean))]
    if (!productId || uniqueComponentIds.length === 0) return

    const { data: components, error: componentsError } = await supabase
      .from('components')
      .select('id, supplier_id')
      .in('id', uniqueComponentIds)
      .not('supplier_id', 'is', null)

    if (componentsError) {
      console.error('Error loading component suppliers for product inheritance:', componentsError)
      return
    }

    const supplierIds = [...new Set((components ?? []).map((c) => c.supplier_id).filter(Boolean))] as string[]
    if (supplierIds.length === 0) return

    const nowIso = new Date().toISOString().slice(0, 10)

    const payload = supplierIds.map((supplierId) => ({
      supplier_id: supplierId,
      product_id: productId,
      approved_for_production: true,
      qualification_date: nowIso,
      qualification_notes: 'Auto-linked from product BOM component supplier inheritance',
    }))

    const { error: linkError } = await supabase
      .from('supplier_products')
      .upsert(payload, { onConflict: 'supplier_id,product_id', ignoreDuplicates: false })

    if (linkError) {
      console.error('Error inheriting suppliers from components to product:', linkError)
    }
  },

  /**
   * Get all components for a specific product (BOM)
   */
  async getProductComponents(productId: string): Promise<ProductComponentWithDetails[]> {
    const { data, error } = await supabase
      .from('product_components')
      .select(`
        *,
        component:components!component_id(
          id,
          name,
          part_number,
          category,
          manufacturer_name,
          ce_marked,
          ukca_marked,
          unit_price,
          currency,
          stock_quantity,
          min_stock_level,
          location,
          purpose
        ),
        added_by_profile:profiles!product_components_added_by_fkey(full_name)
      `)
      .eq('product_id', productId)
      .order('level')
      .order('created_at')

    if (error) {
      console.error('Error fetching product components:', error)
      return []
    }

    return (data as ProductComponentWithDetails[]) ?? []
  },

  /**
   * Add a component to a product
   */
  async addComponentToProduct(
    productComponent: Omit<ProductComponentInsert, 'organization_id'>
  ): Promise<ProductComponent | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('product_components')
      .insert({
        ...productComponent,
        organization_id: orgId,
        added_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error adding component to product:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'linked',
        entityType: 'product_component',
        entityId: data.id,
        details: {
          product_id: productComponent.product_id,
          component_id: productComponent.component_id,
          quantity: productComponent.quantity,
        },
      })

      await this.syncProductSuppliersFromComponents(productComponent.product_id, [productComponent.component_id])
    }

    return data
  },

  /**
   * Update a product-component relationship
   */
  async updateProductComponent(
    id: string,
    updates: ProductComponentUpdate
  ): Promise<ProductComponent | null> {
    const { data, error } = await supabase
      .from('product_components')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating product component:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Remove a component from a product
   */
  async removeComponentFromProduct(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('product_components')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error removing component from product:', error)
      throw toError(error)
    }

    activityLogService.log({
      action: 'unlinked',
      entityType: 'product_component',
      entityId: id,
    })

    return true
  },

  /**
   * Check if a component is already in a product
   */
  async isComponentInProduct(productId: string, componentId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('product_components')
      .select('id')
      .eq('product_id', productId)
      .eq('component_id', componentId)
      .maybeSingle()

    if (error) {
      console.error('Error checking component in product:', error)
      return false
    }

    return !!data
  },

  /**
   * Get BOM statistics for a product
   */
  async getProductBomStats(productId: string): Promise<{
    totalComponents: number
    totalCost: number
    currency: string
    ceMarkedCount: number
    ukca_markedCount: number
    lowStockCount: number
  }> {
    const components = await this.getProductComponents(productId)

    let totalCost = 0
    let ceMarkedCount = 0
    let ukca_markedCount = 0
    let lowStockCount = 0

    // Track currency frequency to pick the predominant one
    const currencyCounts = new Map<string, number>()

    components.forEach((pc) => {
      if (pc.component.unit_price) {
        totalCost += pc.component.unit_price * pc.quantity
      }
      const cur = pc.component.currency || 'USD'
      currencyCounts.set(cur, (currencyCounts.get(cur) ?? 0) + 1)
      if (pc.component.ce_marked) ceMarkedCount++
      if (pc.component.ukca_marked) ukca_markedCount++
      if (pc.component.min_stock_level && (pc.component.stock_quantity ?? 0) < pc.component.min_stock_level) lowStockCount++
    })

    // Pick the most common currency among this product's components
    let currency = 'USD'
    let maxCount = 0
    for (const [cur, count] of currencyCounts) {
      if (count > maxCount) {
        maxCount = count
        currency = cur
      }
    }

    return {
      totalComponents: components.length,
      totalCost,
      currency,
      ceMarkedCount,
      ukca_markedCount,
      lowStockCount,
    }
  },

  /**
   * Get all products that use a specific component
   */
  async getProductsUsingComponent(componentId: string): Promise<Array<{
    id: string
    product_id: string
    product_name: string
    quantity: number
    unit: string
  }>> {
    const { data, error } = await supabase
      .from('product_components')
      .select(`
        id,
        product_id,
        quantity,
        unit,
        product:products(name)
      `)
      .eq('component_id', componentId)

    if (error) {
      console.error('Error fetching products using component:', error)
      return []
    }

    return (data as any[])
      .filter(item => item.product)
      .map(item => ({
        id: item.id,
        product_id: item.product_id,
        product_name: item.product.name,
        quantity: item.quantity,
        unit: item.unit,
      }))
  },

  /**
   * Bulk add components to a product (useful for importing BOMs)
   */
  async bulkAddComponentsToProduct(
    productId: string,
    components: Array<{
      component_id: string
      quantity: number
      unit?: string
      reference_designator?: string
      assembly_notes?: string
    }>
  ): Promise<ProductComponent[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const inserts = components.map(comp => ({
      organization_id: orgId,
      product_id: productId,
      component_id: comp.component_id,
      quantity: comp.quantity,
      unit: comp.unit || 'pcs',
      reference_designator: comp.reference_designator || null,
      assembly_notes: comp.assembly_notes || null,
      added_by: user?.id,
    }))

    const { data, error } = await supabase
      .from('product_components')
      .insert(inserts)
      .select()

    if (error) {
      console.error('Error bulk adding components:', error)
      throw toError(error)
    }

    await this.syncProductSuppliersFromComponents(productId, components.map((c) => c.component_id))

    return data ?? []
  },

  /**
   * Get components shared between this product and other products
   */
  async getCommonComponentsForProduct(productId: string): Promise<Array<{
    component_id: string
    component_name: string
    part_number: string
    products_using_count: number
    other_product_names: string[]
  }>> {
    // Get this product's component IDs
    const productComponents = await this.getProductComponents(productId)
    if (productComponents.length === 0) return []

    // For each component, find other products using it
    const results: Array<{
      component_id: string
      component_name: string
      part_number: string
      products_using_count: number
      other_product_names: string[]
    }> = []

    for (const pc of productComponents) {
      const usages = await this.getProductsUsingComponent(pc.component_id)
      const otherProducts = usages.filter(u => u.product_id !== productId)
      if (otherProducts.length > 0) {
        results.push({
          component_id: pc.component_id,
          component_name: pc.component.name,
          part_number: pc.component.part_number,
          products_using_count: usages.length,
          other_product_names: otherProducts.map(u => u.product_name),
        })
      }
    }

    return results
  },

  /**
   * Build a tree structure from flat BOM list for visualization.
   * Uses parent_product_component_id (correct BOM-row parenting)
   * with fallback to legacy parent_component_id.
   */
  buildBomTree(components: ProductComponentWithDetails[]): BomTreeNode[] {
    // Convert components to tree nodes
    const nodeMap = new Map<string, BomTreeNode>()

    components.forEach((pc) => {
      const node: BomTreeNode = {
        id: pc.id,
        componentId: pc.component_id,
        name: pc.component.name,
        partNumber: pc.component.part_number,
        category: pc.component.category,
        quantity: pc.quantity,
        unit: pc.unit || 'pcs',
        referenceDesignator: pc.reference_designator,
        ceMarked: pc.component.ce_marked,
        ukcaMarked: pc.component.ukca_marked,
        stockQuantity: pc.component.stock_quantity,
        unitPrice: pc.component.unit_price,
        currency: pc.component.currency,
        level: pc.level ?? 0,
        children: [],
        isLowStock: !!(pc.component.min_stock_level && (pc.component.stock_quantity ?? 0) < pc.component.min_stock_level),
        supplierName: pc.component.manufacturer_name,
        manufacturerName: pc.component.manufacturer_name,
      }
      nodeMap.set(pc.id, node)
    })

    // Build tree structure using parent_product_component_id (preferred)
    // with fallback to legacy parent_component_id
    const rootNodes: BomTreeNode[] = []

    // Index by component_id for legacy parent_component_id lookups
    const componentIdToNodeId = new Map<string, string>()
    components.forEach((pc) => {
      componentIdToNodeId.set(pc.component_id, pc.id)
    })

    components.forEach((pc) => {
      const node = nodeMap.get(pc.id)
      if (!node) return

      let parentNodeId: string | null = null
      if (pc.parent_product_component_id) {
        parentNodeId = pc.parent_product_component_id
      } else if (pc.parent_component_id) {
        parentNodeId = componentIdToNodeId.get(pc.parent_component_id) ?? null
      }

      if (parentNodeId) {
        const parent = nodeMap.get(parentNodeId)
        if (parent) {
          parent.children.push(node)
        } else {
          rootNodes.push(node)
        }
      } else {
        rootNodes.push(node)
      }
    })

    // Sort children by name for consistent display
    const sortNodes = (nodes: BomTreeNode[]) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name))
      nodes.forEach(node => sortNodes(node.children))
    }
    sortNodes(rootNodes)

    return rootNodes
  },
}
